import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import {
  Input,
  Output,
  OnInit,
  OnDestroy,
  Component,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { t } from '@lib/translation/helpers';

import { IEmailConfirmResponse, ActivateService, AuthActivateStepStatus } from '../activate';

@Component({
  selector: 'sn-auth-confirm-email',
  templateUrl: './confirm-email.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmEmailContainer implements OnInit, OnDestroy {
  @Input()
  public uid: string;

  @Input()
  public token: string;

  @Output()
  public readonly confirm = new EventEmitter<IEmailConfirmResponse>();

  public label: string;

  public status: AuthActivateStepStatus;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _activateService: ActivateService,
  ) {
    this._updateStatus(AuthActivateStepStatus.WAIT);
  }

  get isLoading(): boolean {
    return this.status === AuthActivateStepStatus.PROGRESS;
  }

  public ngOnInit(): void {
    this.confirmEmail();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public confirmEmail(): void {
    this._updateStatus(AuthActivateStepStatus.PROGRESS);

    this._activateService
      .confirmEmail(this.uid, this.token)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this._changeDetectorRef.markForCheck()),
      )
      .subscribe({
        next: (response) => {
          this._updateStatus(AuthActivateStepStatus.SUCCESS);
          this.confirm.emit(response);
        },
        error: () => this._updateStatus(AuthActivateStepStatus.ERROR),
      });
  }

  private _updateStatus(status: AuthActivateStepStatus): void {
    this.status = status;

    switch (status) {
      case AuthActivateStepStatus.PROGRESS:
        this.label = t('auth.confirmEmail.status.progress');
        break;

      case AuthActivateStepStatus.SUCCESS:
        this.label = t('auth.confirmEmail.status.success');
        break;

      default:
        this.label = t('auth.confirmEmail.status.default');
    }
  }
}
